import { Directive, ElementRef, forwardRef, HostListener, inject, Input, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { noop } from 'rxjs';
import { AbstractDebounceDirective } from '../abstract-debounce/abstract-debounce.directive';

@Directive({
  standalone: true,
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[restrictedInput]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RestrictInputDirective),
      multi: true
    }
  ]
})
export class RestrictInputDirective extends AbstractDebounceDirective implements ControlValueAccessor {
  regex!: RegExp;
  patternEnabled = false;
  uppercaseEnabled = false;
  preValue: string | number = '';
  /** True while handling an event that removes characters, so an intermediate invalid value is not reverted. */
  private isDeletion = false;
  private elementRef: ElementRef = inject(ElementRef);
  private renderer2: Renderer2 = inject(Renderer2);

  @Input() set pattern(value: string | RegExp) {
    this.patternEnabled = !!value;
    this.regex = value instanceof RegExp ? value : new RegExp(value);
  }

  @Input() set uppercase(value: boolean) {
    this.uppercaseEnabled = !!value;
  }

  public onChange: (value: string | number) => void = () => noop();
  public onTouch: () => void = () => noop();

  @HostListener('input', ['$event'])
  public onInput(e: Event): void {
    e.preventDefault();
    this.emitEvent$.next(e);
  }

  @HostListener('change', ['$event'])
  public onInputChange(e: Event): void {
    e.preventDefault();
    this.emitEvent$.next(e);
  }

  onEvent(e: Event): void {
    this.isDeletion = this.isDeletionEvent(e);
    try {
      const target = e?.target as HTMLInputElement | null;
      this.writeValue(target?.value ?? '');
    } finally {
      this.isDeletion = false;
    }
  }

  /**
   * Detects whether the user is removing characters (Backspace, Delete, cut, word/line deletion).
   * Native `InputEvent.inputType` exposes every deletion intent as a `delete*` value; when it is not
   * available (`change` events, older browsers) fall back to comparing the length with the last accepted value.
   */
  private isDeletionEvent(e: Event): boolean {
    const inputType = (e as InputEvent)?.inputType;
    if (typeof inputType === 'string') {
      return inputType.startsWith('delete');
    }
    const target = e?.target as HTMLInputElement | null;
    return `${target?.value ?? ''}`.length < `${this.preValue ?? ''}`.length;
  }

  @HostListener('blur')
  public onBlur(): void {
    this.onTouch();
  }

  public validateValue(value: string) {
    return this.regex?.test(value);
  }

  /** It writes the value in the input */
  public writeValue(value: string | number): void {
    if (this.patternEnabled) {
      const isEmptyValue = value === undefined || value === null || value === '';
      if (!this.regex) {
        throw new Error('No pattern found');
      }
      const isValid = this.validateValue(value as string);
      if (isValid) {
        this.preValue = value;
      } else if (!isEmptyValue && !this.isDeletion) {
        // Reject the typed character and restore the last accepted value.
        value = this.preValue;
      } else {
        // Clearing the field or removing characters is always allowed, even when the
        // intermediate value does not match the pattern yet: remember it as the new baseline.
        this.preValue = value;
      }
    }
    if (this.uppercaseEnabled) {
      value = value?.toString()?.toUpperCase();
    }
    try {
      this.onChange(value);
    } catch (ex) {
      /* empty */
    }
    this.renderer2.setProperty(this.elementRef.nativeElement, 'value', value);
  }

  public registerOnChange(fn: (value: string | number) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }
}
